import React, { forwardRef, useEffect, useState } from 'react'
import { getUserType, downloadDronesReportCSV, getReportStats } from '../../api'
import { GCS_FEATURES, getFeaturesPermissions } from '../../Teams'
import { checkSubscriptionAndStorage, Popup } from '../../ReusableComponents/reusableFunctions'
import { BreadCrumbs } from '../../ReusableComponents/BreadCrumbs'
import LoaderComponent from '../../ReusableComponents/LoaderComponent'
import "./WeeklyReport.css"
import WeekTile from './WeekTile'
import AppConstants from '../../AppConstants'
import SubscriptionExpireModal from '../../subscription/Reusable/SubscriptionExpireModal'
import ReportsFilter from './ReportsFilter'
import ButtonWithIcon from '../../ReusableComponents/ButtonWithIcon'
import BlueArrowUp from '../../icons/BlueArrowUp.svg'
import BlueArrowDown from '../../icons/BlueArrowDown.svg'
import { ReactComponent as SortAscIcon } from '../../icons/ci_sort-ascending.svg'
import { ReactComponent as SortDescIcon } from '../../icons/ci_sort-descending.svg'
import SelectVariants from './selectDropdown'
import maintenanceReport from '../../icons/maintainceReport.svg'
import dateFormat from 'dateformat';

const getUpcomingLabel = (tab) => {
    switch (tab) {
        case "Daily": return "Upcoming Day";
        case "Weekly": return "Upcoming Week";
        case "Monthly": return "Upcoming Month";
        case "Yearly": return "Upcoming Year";
        default: return "Upcoming";
    }
};

const WeeklyReport = (props) => {

    const [loading, setLoading] = useState(true)
    const [permissions, setPermissions] = useState(null)
    const [user, setUser] = useState(null)
    const [showSubscriptionExpirePopup, setShowSubscriptionExpirePopup] = useState(undefined)
    const [statsData, setStatsData] = useState([]);
    const [filterState, setFilterState] = useState()
    const [reportCount, setReportCount] = useState(0);
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
    const [yearType, setYearType] = useState('Custom');
    const [firstRender, setFirstRender] = useState(true)
    const [tileGridData, setTileGridData] = useState([])
    const [statsLoading, setStatsLoading] = useState(false)
    const [sortOrder, setSortOrder] = useState('desc');
    const [dateRange, setDateRange] = useState({ from: null, to: null });
    const [toggleChecked, setToggleChecked] = useState(false)
    const [reportType, setReportType] = useState("All Drones")

    const tabToIntervalType = {
        "Daily": "day",
        "Weekly": "week",
        "Monthly": "month",
        "Yearly": "year",
        "Custom": "custom"
    };

    const reportOptions = [
        { id: 1, placeholder: "All Drones" },
        { id: 2, placeholder: "Maintenance Drones" },
    ];

    useEffect(() => {
        document.title = "Reports - AeroGCS Enterprise";
        if (firstRender) {
            checkSubscriptionAndStorage(AppConstants.SERVICES.AEROGCS).then(data => {
                if (data.error || data.storageExceed || data.isExpired || data.subscriptionExpire || !data.isValidStoragePlan) setShowSubscriptionExpirePopup({ ...data, user: user });
                else setShowSubscriptionExpirePopup(false)
            })
            setFirstRender(false)
            initData()
        }
    }, [])

    const initData = async () => {
        try {
            const gUser = props?.location?.state?.user
            if (gUser) {
                setUser(gUser)
                const permissions = await getFeaturesPermissions([GCS_FEATURES.PLANS])
                setPermissions(permissions)
            } else {
                const [blah, type, user] = await getUserType()
                setUser(user)
                const permissions = await getFeaturesPermissions([GCS_FEATURES.PLANS])
                setPermissions(permissions)
            }
        } catch (err) {
            Popup.alert("ERROR", err.message || "Something went wrong while loading page")
        }
    }

    useEffect(() => {
        fetchStats()
    }, [filterState, reportType])

    const fetchStats = async () => {
        try {
            setStatsLoading(true)
            if (filterState?.selectedTab && filterState?.fromDateTime && filterState?.toDateTime) {
                const intervalType = tabToIntervalType[filterState?.selectedTab] || "day";
                const fromDate = new Date(filterState?.fromDateTime).toISOString()
                const toDate = new Date(filterState?.toDateTime).toISOString()
                const pilotId = filterState?.selectedPilots?.[0]?.value;
                const droneId = filterState?.selectedDrones?.[0]?.value;
                const stats = await getReportStats({ fromDate, toDate, intervalType, pilotId, droneId, reportType });
                const reportCount = stats.filter(stat => stat.total_drones_utilize > 0 && new Date(stat.interval_start * 1000) < new Date()).length;
                setStatsData(stats);
                setReportCount(reportCount);
            } else {
                throw ("Incomplete filter data");
            }
        } catch (e) {
            setStatsData([]);
            setReportCount(0);
        } finally {
            setStatsLoading(false)
            setLoading(false)
        }
    };

    const findStatsForPeriod = (start, intervalType = "day") => {
        const startDate = new Date(start);
        return statsData.find(stat => {
            const statDate = new Date(stat.interval_start * 1000);
            switch (intervalType) {
                case "year":
                    return statDate.getFullYear() === startDate.getFullYear();
                case "month":
                    return (statDate.getFullYear() === startDate.getFullYear() && statDate.getMonth() === startDate.getMonth());
                case "week": {
                    const getWeek = date => {
                        const firstDay = new Date(date.getFullYear(), 0, 1);
                        const days = Math.floor((date - firstDay) / 86400000);
                        return Math.ceil((days + firstDay.getDay() + 1) / 7);
                    };
                    return (statDate.getFullYear() === startDate.getFullYear() && getWeek(statDate) === getWeek(startDate));
                }
                case "day":
                default:
                    return (statDate.getFullYear() === startDate.getFullYear() && statDate.getMonth() === startDate.getMonth() && statDate.getDate() === startDate.getDate());
            }
        });
    };

    const generateDays = (selectedMonth, selectedYear) => {
        const startDate = new Date(selectedYear, selectedMonth, 1);
        const endDate = new Date(selectedYear, selectedMonth + 1, 0);

        const days = [];
        let current = new Date(startDate);

        while (current <= endDate) {
            const startOfDay = new Date(current.getFullYear(), current.getMonth(), current.getDate(), 0, 0, 0, 0);
            const endOfDay = new Date(current.getFullYear(), current.getMonth(), current.getDate(), 23, 59, 59, 999);
            const isAfterToday = current > new Date();
            const isBeforeCreated = current < new Date(user.created_on)
            days.push({
                start: startOfDay,
                end: endOfDay,
                isDisabled: isAfterToday || isBeforeCreated,
            });
            current.setDate(current.getDate() + 1);
        }
        return days;
    };

    const getWeeksInMonth = (month, year, fromDate, toDate) => {
        const startOfMonth = new Date(year, month, 1);
        const endOfMonth = new Date(year, month + 1, 0, 23, 59, 59, 999);

        const currentDate = toDate ? new Date(toDate) : new Date();
        currentDate.setHours(23, 59, 0, 0);

        const startOfWeek = (date) => {
            const d = new Date(date);
            const day = d.getDay();
            const diff = d.getDate() - day;
            return new Date(d.getFullYear(), d.getMonth(), diff, 0, 0, 0, 0);
        };

        const endOfWeek = (date) => {
            const d = new Date(date);
            const day = d.getDay();
            const diff = d.getDate() + (6 - day);
            return new Date(d.getFullYear(), d.getMonth(), diff, 23, 59, 59, 999);
        };

        const weeks = [];
        let currentWeekStart = startOfWeek(startOfMonth);
        let currentWeekEnd = endOfWeek(currentWeekStart);

        const fromDateObj = new Date(fromDate);

        while (currentWeekStart <= endOfMonth) {
            const isFutureWeek = currentWeekStart > currentDate;
            const isInvalidWeek = currentWeekStart < startOfWeek(fromDateObj);
            const isCurrentWeek = currentWeekStart <= currentDate && currentWeekEnd >= currentDate;

            weeks.push({
                start: new Date(currentWeekStart),
                end: new Date(currentWeekEnd),
                isDisabled: isFutureWeek || isInvalidWeek,
                isCurrent: isCurrentWeek,
            });
            currentWeekStart = new Date(currentWeekStart);
            currentWeekStart.setDate(currentWeekStart.getDate() + 7);
            currentWeekEnd = endOfWeek(currentWeekStart);
        }
        return weeks;
    };

    const generateMonths = (selectedYear) => {
        const startDate = new Date(selectedYear, 0, 1);
        const endDate = new Date(selectedYear, 11, 31);
        const now = new Date();

        const months = [];
        let current = new Date(startDate);
        while (current <= endDate) {
            const monthStart = new Date(current.getFullYear(), current.getMonth(), 1, 0, 0, 0, 0);
            const monthEnd = new Date(current.getFullYear(), current.getMonth() + 1, 0, 23, 59, 59, 999);

            const isAfterNow = current.getFullYear() > now.getFullYear() || (current.getFullYear() === now.getFullYear() && current.getMonth() > now.getMonth());
            const isBeforeCreated = user?.created_on && (current.getFullYear() < new Date(user.created_on).getFullYear() || (current.getFullYear() === new Date(user.created_on).getFullYear() && current.getMonth() < new Date(user.created_on).getMonth()));

            months.push({
                start: monthStart,
                end: monthEnd,
                isDisabled: isAfterNow || isBeforeCreated,
            });
            current.setMonth(current.getMonth() + 1);
        }
        return months;
    };

    const generateYears = (yearType, user) => {
        const startYear = new Date(user?.created_on).getFullYear();
        const currentYear = new Date().getFullYear();

        const years = [];
        for (let year = startYear; year <= currentYear; year++) {
            if (yearType === "Financial") {
                years.push({
                    name: `${year}-${year + 1}`,
                    start: new Date(year, 3, 1, 0, 0, 0, 0),
                    end: new Date(year + 1, 2, 31, 23, 59, 59, 999),
                    isDisabled: false,
                });
            } else {
                years.push({
                    name: `${year}`,
                    start: new Date(year, 0, 1, 0, 0, 0, 0),
                    end: new Date(year, 11, 31, 23, 59, 59, 999),
                    isDisabled: false,
                });
            }
        }
        return years;
    };

    const onFilterStateChange = async (data, month, year, year_type) => {
        const isTabChanged = data.selectedTab !== filterState?.selectedTab;

        if (isTabChanged && data.selectedTab !== 'Custom') {
            month = new Date().getMonth();
            year = new Date().getFullYear();
            setCurrentMonth(month);
            setCurrentYear(year);
        }

        let fromDateTime;
        let toDateTime;
        let tileGrid = [];

        if (data.selectedTab === 'Daily') {
            const days = generateDays(month, year);
            tileGrid = days;
            fromDateTime = days?.[0]?.start;
            toDateTime = days?.[days.length - 1]?.end;
        } else if (data.selectedTab === 'Weekly') {
            let weeks = getWeeksInMonth(month, year, user?.created_on, new Date());
            weeks = weeks.map((tile, i) => ({ ...tile, weekOfMonth: i + 1 }));
            tileGrid = weeks;
            fromDateTime = weeks?.[0]?.start;
            toDateTime = weeks?.[weeks.length - 1]?.end;
        } else if (data.selectedTab === 'Monthly') {
            const months = generateMonths(year, user);
            tileGrid = months;
            fromDateTime = months?.[0]?.start;
            toDateTime = months?.[months.length - 1]?.end;
        } else if (data.selectedTab === 'Yearly') {
            const years = generateYears(year_type || yearType, user);
            tileGrid = years;
            fromDateTime = years?.[0]?.start;
            toDateTime = years?.[years.length - 1]?.end;
        } else if (data.selectedTab === 'Custom') {
            fromDateTime = data.fromDateTime || filterState?.fromDateTime;
            toDateTime = data.toDateTime || filterState?.toDateTime;
            if (fromDateTime && toDateTime) {
                tileGrid = [{
                    start: new Date(fromDateTime),
                    end: new Date(toDateTime),
                    isDisabled: false,
                    name: 'Custom Range',
                }];
            }
        }

        if (tileGrid.length > 0 && data.selectedTab !== 'Custom') {
            let filteredTileGrid = tileGrid;
            if (data.selectedTab === 'Weekly') {
                const currentDate = new Date();
                filteredTileGrid = tileGrid.filter(tile => tile.start <= currentDate);
            }
            setDateRange({
                from: filteredTileGrid[0]?.start || tileGrid[0]?.start,
                to: filteredTileGrid[filteredTileGrid.length - 1]?.end || tileGrid[tileGrid.length - 1]?.end,
            });
        }

        setFilterState({
            ...data,
            fromDateTime: fromDateTime,
            toDateTime: toDateTime,
        });

        setTileGridData(
            [...tileGrid].sort((a, b) =>
                sortOrder === 'asc'
                    ? a.start.getTime() - b.start.getTime()
                    : b.start.getTime() - a.start.getTime()
            )
        );
    };

    useEffect(() => {
        if (tileGridData.length > 0) {
            setTileGridData(prev =>
                [...prev].sort((a, b) =>
                    sortOrder === "asc" ? a.start.valueOf() - b.start.valueOf() : b.start.valueOf() - a.start.valueOf()
                )
            );
        }
    }, [sortOrder]);

    const generateMonthOptions = () => {
        const userCreatedDate = new Date(user?.created_on);
        const createdYear = userCreatedDate.getFullYear();
        const createdMonth = userCreatedDate.getMonth();

        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYearNow = now.getFullYear();

        const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

        return Array.from({ length: 12 }, (_, i) => {
            if (currentYear === createdYear && i < createdMonth) return null;
            if (currentYear === currentYearNow && i > currentMonth) return null;
            return { label: monthNames[i], value: i };
        }).filter(Boolean);
    }

    const generateYearOptions = () => {
        const userCreatedDate = new Date(user?.created_on);
        const createdYear = userCreatedDate.getFullYear();
        const currentYear = new Date().getFullYear();

        return Array.from(
            { length: currentYear - createdYear + 1 },
            (_, i) => {
                const year = currentYear - i;
                return {
                    label: year.toString(),
                    value: year
                };
            }
        );
    }

    const handleTileClick = async (period, fileType) => {
        try {
            Popup.alert("WAIT", 'Generating report file, please wait...');
            await downloadDronesReportCSV({
                fromDate: new Date(period.start).toISOString(),
                toDate: new Date(period.end).toISOString(),
                pilotId: filterState.selectedPilots?.[0]?.value || undefined,
                droneId: filterState.selectedDrones?.[0]?.value || undefined,
                intervalType: tabToIntervalType[filterState?.selectedTab] || "day",
                fileType: fileType,
                alldrone: toggleChecked ? true : false,
                reportType,
            });
            Popup.alert('COMPLETE', 'File downloaded successfully.')
        } catch (e) {
            Popup.alert('ERROR', e.message || e);
        }
    };
    return (
        <div className='wrapper'>
            <div className="right-content-new">
                {showSubscriptionExpirePopup && <SubscriptionExpireModal data={showSubscriptionExpirePopup} />}
                <div className="top-bar-new">
                    <div className="top-bar-text-new">
                        <BreadCrumbs data={[{ displayName: "Home", pathname: "/", state: { user } }, { displayName: 'Reports' }]} />
                    </div>
                </div>
                <div className='main-content-new' style={{ paddingTop: "20px", overflow: 'auto' }}>
                    {loading ? <LoaderComponent />
                        : <div style={{ height: "100%", display: "flex", flexDirection: "column", scrollBehavior: "smooth", }}>
                            <div style={{ fontWeight: '600', fontSize: '25px', marginRight: 'auto' }}>Reports</div>
                            <ReportsFilter
                                stats={{ "Total Reports": reportCount }}
                                selectedTab={filterState?.selectedTab || "Daily"}
                                isPilotEnable={true}
                                reportType={reportType}
                                defaultFromDateTime={dateRange.from ? new Date(dateRange.from) : null}
                                defaultToDateTime={dateRange.to ? new Date(dateRange.to) : null}
                                onFilterDataChange={(data) => onFilterStateChange(data, currentMonth, currentYear, yearType)}
                                onChangeCheckbox={(checked) => {
                                    setToggleChecked(checked)
                                }}
                            />
                            <div className="daily-tab-section" style={{ background: "#fff", padding: "30px", boxShadow: "0 1px 4px rgba(0,0,0,0.1)", borderRadius: "0px 0px 10px 10px", }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: "15px", borderBottom: "2px solid #eee", paddingBottom: "20px", gap: '10px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                                        {(filterState?.selectedTab !== "Custom") &&
                                            <>{(filterState?.selectedTab === "Yearly") ? <h4 style={{ marginBottom: "0px" }}>
                                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                                    <SelectVariants
                                                        value={yearType}
                                                        onChange={(e) => {
                                                            setYearType(e.target.value)
                                                            onFilterStateChange(filterState, currentMonth, currentYear, e.target.value)
                                                        }}
                                                        options={[
                                                            { label: 'Calendar Year', value: 'Custom' },
                                                            { label: 'Financial Year', value: 'Financial' }
                                                        ]}
                                                        sx={{ minWidth: 150 }}
                                                    />
                                                </div>
                                            </h4>
                                                : <h4 style={{ marginBottom: "0px", display: 'flex', justifyContent: "space-between", gap: '25px' }}>
                                                    {(filterState?.selectedTab == "Daily" || filterState?.selectedTab == "Weekly") && <div style={{ position: 'relative', display: 'inline-block' }}>
                                                        <SelectVariants
                                                            value={currentMonth}
                                                            onChange={(e) => {
                                                                const selectedMonth = parseInt(e.target.value);
                                                                setCurrentMonth(selectedMonth);
                                                                onFilterStateChange(
                                                                    filterState,
                                                                    selectedMonth,
                                                                    currentYear,
                                                                    yearType
                                                                );
                                                            }}
                                                            options={generateMonthOptions()}
                                                            sx={{ minWidth: 140 }}
                                                        />
                                                    </div>}
                                                    <div style={{ position: 'relative', display: 'inline-block' }}>
                                                        <SelectVariants
                                                            value={currentYear}
                                                            onChange={(e) => {
                                                                const selectedYear = parseInt(e.target.value);
                                                                const selectedMonth = selectedYear === new Date(user?.created_on).getFullYear() ? new Date(user?.created_on).getMonth() : 0
                                                                setCurrentYear(selectedYear);
                                                                setCurrentMonth(selectedMonth)
                                                                onFilterStateChange(filterState, selectedMonth, selectedYear, yearType)
                                                            }}
                                                            options={generateYearOptions()}
                                                            sx={{ width: "100px !important", }}
                                                        />
                                                    </div>
                                                </h4>}</>}
                                        <div style={{ width: 'auto' }} className="drones_options_selectors">
                                            {reportOptions.map((button, i) => (
                                                <button
                                                    key={button.id}
                                                    style={{
                                                        width: '200px',
                                                        borderLeft: i === 0 && reportType !== button.placeholder && "1px solid #C9C9C9",
                                                        borderRight: reportType !== button.placeholder && "1px solid #C9C9C9",
                                                    }}
                                                    className={reportType === button.placeholder ? "active" : ""}
                                                    onClick={() => {
                                                        setReportType(button.placeholder)
                                                    }}
                                                >
                                                    {button.placeholder}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {(filterState?.selectedTab !== "Custom") && <>{sortOrder === "asc" ? <SortAscIcon style={{ width: "28px", height: "28px", cursor: "pointer" }} onClick={() => setSortOrder(prev => prev === "asc" ? "desc" : "asc")} />
                                        : <SortDescIcon style={{ width: "28px", height: "28px", cursor: "pointer" }} onClick={() => setSortOrder(prev => prev === "asc" ? "desc" : "asc")} />}</>}
                                </div>
                                {statsLoading ? <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                                    <LoaderComponent />
                                </div> :
                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: "35px" }}>
                                        {[...tileGridData].filter(tile => filterState.selectedTab === "Custom" || new Date(tile.start) <= new Date()).sort((a, b) => sortOrder === "asc" ? a.start.valueOf() - b.start.valueOf() : b.start.valueOf() - a.start.valueOf()).map((tile, idx) => {
                                            const statsData = findStatsForPeriod(tile.start) || {};
                                            console.log('statsData', statsData);

                                            return (
                                                <WeekTile
                                                    key={idx}
                                                    period={tile}
                                                    activeReportIcon={reportType === 'Maintenance Drones' ? maintenanceReport : undefined}
                                                    inactiveReportIcon={reportType === 'Maintenance Drones' ? maintenanceReport : undefined}
                                                    handleClick={handleTileClick}
                                                    title={filterState.selectedTab === "Daily" ? dateFormat(tile.start, 'dd mmm yyyy')
                                                        : filterState.selectedTab === "Weekly" ? `Week ${tile.weekOfMonth}`
                                                            : filterState.selectedTab === "Monthly" ? dateFormat(tile.start, 'mmmm')
                                                                : filterState.selectedTab === "Yearly" ? tile.name
                                                                    : filterState.selectedTab === "Custom" ? 'Custom Range'
                                                                        : 'NA'
                                                    }
                                                    subtitle={filterState.selectedTab === "Daily" ? `${dateFormat(tile.start, 'hh:MM TT')} - ${dateFormat(tile.end, 'hh:MM TT')}`
                                                        : filterState.selectedTab === "Weekly" ? `${dateFormat(tile.start, 'dd/mm/yyyy')} - ${dateFormat(tile.end, 'dd/mm/yyyy')}`
                                                            : filterState.selectedTab === "Monthly" ? dateFormat(tile.start, 'yyyy')
                                                                : filterState.selectedTab === "Yearly" ? `${dateFormat(tile.start, 'mmm yyyy')} - ${dateFormat(tile.end, 'mmm yyyy')}`
                                                                    : filterState.selectedTab === "Custom" ? `${dateFormat(tile.start, 'dd/mm/yyyy')} - ${dateFormat(tile.end, 'dd/mm/yyyy')}`
                                                                        : 'NA'
                                                    }
                                                    stats={reportType === 'Maintenance Drones' ?
                                                        {
                                                            totalFlight: { label: "Total Maintenance", value: statsData.total_maintenance ?? 0 },
                                                            activeDrone: { label: "On Going", value: statsData.ongoing_maintenance ?? 0 },
                                                            activePilot: { label: "Timely Deliveries", value: statsData.timely_deliveries ?? 0 },
                                                            acres: { label: "Delayed Deliveries", value: statsData.delayed_deliveries ?? 0 }
                                                        }
                                                        : {
                                                            totalFlight: { label: "Total Flight", value: statsData.total_flights ?? 0 },
                                                            activeDrone: { label: "Active Drone", value: statsData.total_drones_utilize ?? 0 },
                                                            activePilot: { label: "Active Pilot", value: statsData.total_pilots ?? 0 },
                                                            acres: { label: "Acres", value: statsData.total_acres_covered ?? 0 }
                                                        }}
                                                    isDisabledDownload={statsData.total_drones_utilize && statsData.total_drones_utilize != '0' ? false : true}
                                                    isDisabled={tile.isDisabled}
                                                    isUpcoming={new Date(tile.start) > new Date()}
                                                    upcomingLabel={getUpcomingLabel(filterState.selectedTab)}
                                                    reportType={reportType}
                                                />
                                            );
                                        })}
                                    </div>}
                            </div>
                        </div>
                    }
                </div>
            </div>
        </div >
    )
}

export default WeeklyReport